import React from "react";
import { ReactElement } from "react";
import {
  LastenVergelekenHoofdstuk,
  Lasten,
} from "../../../../.generated/rapportage/rapportagetypes";
import Table from "../../components/Table";
import { Column } from "../../components/BaseTable";
import { Chapter } from "../../components/Chapter";
import { Paragraph } from "../../components/Paragraph";
import { bedragFormat } from "../../../../shared/utils/currency";
import Heading from "../../components/Heading";
import { isEmpty } from "../../../utils";
import { LastenInSituatie } from "../../../../.generated/rapportage/rapportagetypes";
import styled from "styled-components";
import { formatDate } from "../../../../components/formatters";

export const calculateVerschilNettoForJaar = (
  data1: Lasten | undefined | null,
  data2: Lasten | undefined | null,
): number | null => {
  if (
    data1 &&
    typeof data1.nettoLast === "number" &&
    data2 &&
    typeof data2.nettoLast === "number"
  ) {
    return data2.nettoLast - data1.nettoLast;
  }
  /* istanbul ignore next */
  return null;
};

const getAnalyseOmschrijving = (analyse: LastenInSituatie): string => {
  if (isEmpty(analyse.omschrijving)) {
    return `Voorstel #${analyse.analyseVolgnummer}`;
  }

  return analyse.omschrijving ?? `Voorstel #${analyse.analyseVolgnummer}`;
};
/* istanbul ignore next */
const getTermijn = (termijn: number | null, bedrag: number | null): string => {
  if (termijn === 99) {
    return `eenmalig ${bedrag ? bedragFormat(bedrag) : "N/A"}`;
  }

  const termijnString = (() => {
    switch (termijn) {
      case 0:
        return "geen";
      case 1:
        return "maand";
      case 2:
        return "twee maanden";
      case 3:
        return "kwartaal";
      case 6:
        return "half jaar";
      case 12:
        return "jaar";
      case 99:
        return "eenmalig";
      case -1:
        return "dag";
      case -7:
        return "week";
      default:
        return "geen";
    }
  })();

  return bedrag
    ? `${bedragFormat(bedrag)} per ${termijnString}`
    : termijnString;
};

type LastenVergelekenProps = {
  lastenVergeleken: LastenVergelekenHoofdstuk;
};
interface GridWrapperProps {
  columns: number;
}

const GridWrapper = styled.div<GridWrapperProps>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.columns}, 1fr);
  gap: 10px;
  padding: 20px;
`;

interface GridColumnProps {
  skipFirstElements?: number;
}

const GridColumn = styled.div<GridColumnProps>`
  display: flex;
  flex-direction: column;

  /* Skip styling for first N elements based on skipFirstElements prop */
  ${(props) => {
    const skip = props.skipFirstElements || 0;

    return `
    > *{
        
    }
      /* First ${skip} children get less padding */
      > *:nth-child(-n + ${skip}) {
        padding-bottom: 5px;
        padding-left: 10px;
      }

      /* All other children get 10px padding */
      > *:nth-child(n + ${skip}) {
        padding: 10px;
        height: 75px;
      }

      /* Style alternating rows within each column, starting after skipped elements */
      > *:nth-child(2n + ${skip}) {
        background-color: #ffffff;
      }

      > *:nth-child(2n + ${skip + 1}) {
        background-color: #f9f9f9;
      }
    `;
  }}
`;

export const getProductenTable = (
  analyseData: LastenInSituatie[] | null,
): ReactElement | null => {
  /* istanbul ignore next */
  if (!analyseData || analyseData.length === 0) {
    return null;
  }

  return (
    <div style={{ marginTop: "20px" }}>
      <GridWrapper columns={analyseData.length}>
        {analyseData.map((analyseItem, index) => (
          // Each analyseItem is a block (column)
          <GridColumn
            key={`analyse-block-${analyseItem.analyseVolgnummer ??
              "unknown"}-${index}`}
            skipFirstElements={2}
          >
            <p>
              <strong>{getAnalyseOmschrijving(analyseItem)}</strong>
            </p>
            {analyseItem?.producten?.map((product, productIndex) => (
              <div key={`product-${productIndex}`}>
                <div>
                  {product.maatschappijNaam} {product.productNaam}{" "}
                  {product.omschrijving} {product.risicoDekkingType}
                </div>
                <div>
                  {typeof product.bedrag === "number"
                    ? bedragFormat(product.bedrag)
                    : product.bedrag}
                </div>
                {product.kapitaalType && (
                  <div>
                    {product.kapitaalType}
                    {product.doelkapitaalBedrag &&
                      typeof product.doelkapitaalBedrag === "number" && (
                        <> {bedragFormat(product.doelkapitaalBedrag)}</>
                      )}
                  </div>
                )}
                {product.rendementPercentage && (
                  <div>
                    Rendement:{" "}
                    {typeof product.rendementPercentage === "number"
                      ? `${String(product.rendementPercentage).replace(
                          ".",
                          ",",
                        )}%`
                      : "-"}
                  </div>
                )}
                {product.einddatumUitkering && (
                  <div>
                    Einddatum uitkering:{" "}
                    {formatDate(product.einddatumUitkering)}
                  </div>
                )}
                {product.uitkeringsBedrag && (
                  <div>
                    Uitkering:{" "}
                    {getTermijn(
                      product.uitkeringsTermijn,
                      product.uitkeringsBedrag,
                    )}
                  </div>
                )}
                {product.dekkingsSoort && <div>{product.dekkingsSoort}</div>}
              </div>
            ))}
          </GridColumn>
        ))}
      </GridWrapper>
    </div>
  );
};

const getLeningdelenTable = (
  analyseData: LastenInSituatie[] | null,
): ReactElement | null => {
  /* istanbul ignore next */
  if (!analyseData || analyseData.length === 0) {
    return null;
  }

  return (
    <div style={{ marginTop: "20px" }}>
      <GridWrapper columns={analyseData.length}>
        {analyseData.map((analyseItem, index) =>
          // Each analyseItem is a block (column)
          !analyseItem.leningdelenDetails ||
          analyseItem.leningdelenDetails.length === 0 ? (
            <p
              key={`analyse-block-${analyseItem.analyseVolgnummer ??
                "unknown"}-${index}`}
              style={{
                textAlign: "center",
                fontStyle: "italic",
                padding: "8px",
                color: "#6c757d",
                fontSize: "0.8rem",
              }}
            >
              Er zijn geen hypotheek gegevens beschikbaar in het voorstel.
            </p>
          ) : (
            <GridColumn
              key={`analyse-block-${analyseItem.analyseVolgnummer ??
                "unknown"}-${index}`}
              skipFirstElements={3}
            >
              <p>
                <strong>{getAnalyseOmschrijving(analyseItem)}</strong>
              </p>
              <p>
                <strong>
                  {analyseItem.lenindelenOverzicht?.[0]?.geldverstrekker}
                </strong>
              </p>
              {analyseItem?.leningdelenDetails?.map((leningdeel, index) => (
                <div key={`leningdeel-${index}`}>
                  <div>
                    {leningdeel.hypotheekvorm || "N/A"}
                    {typeof leningdeel.hypotheekbedrag === "number"
                      ? ` ${bedragFormat(leningdeel.hypotheekbedrag)}`
                      : ""}
                  </div>
                  <div>
                    Rentepercentage:{" "}
                    {typeof leningdeel.rentepercentage === "number"
                      ? `${String(leningdeel.rentepercentage).replace(
                          ".",
                          ",",
                        )}%`
                      : "-"}
                  </div>
                  <div>
                    Rentevastperiode: {leningdeel.rentevastperiodeEinddatum}
                  </div>
                  <div>Looptijd: {leningdeel.looptijd}</div>
                </div>
              ))}
            </GridColumn>
          ),
        )}
      </GridWrapper>
    </div>
  );
};

const getLastenTable = (
  situatieLasten: LastenInSituatie[] | null,
): ReactElement | null => {
  /* istanbul ignore next */
  if (!situatieLasten || situatieLasten.length === 0) {
    return null;
  }

  const lastenColumns: Column<any>[] = [];

  situatieLasten.forEach((item, index) => {
    lastenColumns.push({
      header: "Jaar",
      accessor: `scenario${index}_jaar`,
      align: "left",
    });

    // huur is alleen in huidige situatie aanwezig
    index === 0 &&
      lastenColumns.push({
        header: "Huur",
        accessor: `scenario${index}_huur`,
        align: "right",
        dataType: "currency",
      });

    lastenColumns.push(
      {
        header: `Restant schuld`,
        accessor: `scenario${index}_somHypotheken`,
        align: "right",
        dataType: "currency",
        style: "border-left: 1px solid #e0e0e0;",
      },
      {
        header: "Bruto Last",
        accessor: `scenario${index}_brutoLast`,
        align: "right",
        dataType: "currency",
      },
      {
        header: "Netto",
        accessor: `scenario${index}_nettoLast`,
        align: "right",
        dataType: "currency",
      },
    );
  });

  // Add a difference column if there are exactly two items
  if (situatieLasten && situatieLasten.length === 2) {
    lastenColumns.push({
      header: "Verschil Netto",
      accessor: "verschilNetto",
      align: "right",
      dataType: "currency",
      highlight: true,
    });
  }

  const oneVoorstelBox = (): number[] => {
    return [25, 75];
  };

  const twoVoorstellenBox = (length: number): number[] => {
    const contentWidth = 85;

    return [15, contentWidth / length, contentWidth / length, 15];
  };

  const threeVoorstellenBox = (length: number): number[] => {
    return [10, 90 / length, 90 / length, 90 / length];
  };

  const getBoxWidthPercentage = (length: number, boxIndex: number): number => {
    if (length === 1) {
      return oneVoorstelBox()[boxIndex];
    }
    if (length === 2) {
      return twoVoorstellenBox(length)[boxIndex];
    }

    return threeVoorstellenBox(length)[boxIndex];
  };

  // Get maximum number of years across all analyses
  const maxYears = Math.max(
    ...situatieLasten.map((item) => item.jaarGegevens?.length || 0),
  );

  // Create table data with rows for each year
  const tableData: Record<string, number | null | string>[] = [];

  // For each year index
  for (let yearIndex = 0; yearIndex < maxYears; yearIndex++) {
    const yearRow: Record<string, number | null | string> = {};

    // For each analysis scenario
    situatieLasten.forEach((situatieItem, scenarioIndex) => {
      if (
        situatieItem.jaarGegevens &&
        yearIndex < situatieItem.jaarGegevens.length
      ) {
        const lasten = situatieItem.jaarGegevens[yearIndex];
        yearRow[`scenario${scenarioIndex}_jaar`] =
          scenarioIndex === 0 ? lasten.jaar : null;
        yearRow[`scenario${scenarioIndex}_huur`] = lasten.huur;
        yearRow[`scenario${scenarioIndex}_somHypotheken`] =
          lasten.somHypotheken;
        yearRow[`scenario${scenarioIndex}_brutoInkomen`] = lasten.brutoInkomen;
        yearRow[`scenario${scenarioIndex}_brutoLast`] = lasten.brutoLast;
        yearRow[`scenario${scenarioIndex}_nettoLast`] = lasten.nettoLast;
        yearRow[`scenario${scenarioIndex}_vrh`] = lasten.vrh;
      }
    });

    // Calculate and add the difference if there are two items
    if (situatieLasten.length === 2) {
      const lastenData1 = situatieLasten[0]?.jaarGegevens?.[yearIndex];
      const lastenData2 = situatieLasten[1]?.jaarGegevens?.[yearIndex];

      yearRow["verschilNetto"] = calculateVerschilNettoForJaar(
        lastenData1,
        lastenData2,
      );
    }

    tableData.push(yearRow);
  }

  /* istanbul ignore next */
  if (situatieLasten.length > 0) {
    const totalRow: Record<string, number | null | string> = {};

    situatieLasten.forEach((situatieItem, scenarioIndex) => {
      if ((situatieItem as any).totalen) {
        const totalen = (situatieItem as any).totalen;
        totalRow[`scenario${scenarioIndex}_jaar`] =
          scenarioIndex === 0 ? "Totaal" : null;
        totalRow[`scenario${scenarioIndex}_huur`] = totalen.huur || 0;
        totalRow[`scenario${scenarioIndex}_somHypotheken`] =
          totalen.somHypotheken;
        totalRow[`scenario${scenarioIndex}_brutoInkomen`] =
          totalen.brutoInkomen;
        totalRow[`scenario${scenarioIndex}_brutoLast`] = totalen.brutoLast;
        totalRow[`scenario${scenarioIndex}_nettoLast`] = totalen.nettoLast;
        totalRow[`scenario${scenarioIndex}_vrh`] = totalen.vrh;
      }
    });

    // Calculate difference in totals if there are two analyses selected
    if (situatieLasten.length === 2) {
      const totalen1 = (situatieLasten[0] as any).totalen;
      const totalen2 = (situatieLasten[1] as any).totalen;

      if (
        totalen1 &&
        typeof totalen1.nettoLast === "number" &&
        totalen2 &&
        typeof totalen2.nettoLast === "number"
      ) {
        const verschilNetto = totalen2.nettoLast - totalen1.nettoLast;
        totalRow["verschilNetto"] = verschilNetto;
      }
    }

    tableData.push(totalRow);
  }

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
        }}
      >
        <div
          style={{
            width: `${getBoxWidthPercentage(situatieLasten.length, 0)}%`,
          }}
        ></div>
        {situatieLasten.map((analyse, index) => (
          <div
            key={`analyse-${analyse.analyseVolgnummer ?? "unknown"}-${index}`}
            style={{
              padding: "7px",
              textAlign: "left",
              width: `${getBoxWidthPercentage(
                situatieLasten.length,
                index + 1,
              )}%`,
            }}
          >
            <strong>{getAnalyseOmschrijving(analyse)}</strong>
          </div>
        ))}
        {situatieLasten.length === 2 && (
          <div
            style={{
              width: `${getBoxWidthPercentage(situatieLasten.length, 3)}%`,
            }}
          ></div>
        )}
      </div>
      <Table
        columns={lastenColumns}
        data={tableData}
        id={"lasten-vergeleken-table"}
        aria-labelledby="Lasten-vergelijken"
        compact
        includeFooter
      />
    </>
  );
};

export const LastenVergelekenChapter = ({
  lastenVergeleken,
}: LastenVergelekenProps): ReactElement => {
  // Controleer of er naast leningdelen nog andere producten aanwezig zijn
  const geenProducten =
    lastenVergeleken?.situatieLasten?.every(
      (item) => !item.producten || item.producten.length === 0,
    ) ?? true;

  return (
    <Chapter>
      <div className={"break-avoid"}>
        <Heading level={2} text="Overzicht vergeleken voorstellen" />
        <Paragraph paragraaf={lastenVergeleken?.paragrafen?.["3051001"]} />

        <Heading level={3} text="Hypotheek in voorstel" />
        {lastenVergeleken.situatieLasten &&
          lastenVergeleken.situatieLasten.length > 0 &&
          getLeningdelenTable(lastenVergeleken.situatieLasten)}
      </div>
      {!geenProducten && (
        <div className="break-avoid">
          <Heading level={3} text="Producten in voorstel" />
          {lastenVergeleken.situatieLasten &&
            lastenVergeleken.situatieLasten.length > 0 &&
            getProductenTable(lastenVergeleken.situatieLasten)}
        </div>
      )}
      <div className={"break-avoid"}>
        <Heading level={3} text="Lasten vergelijken" />
        <p>Hieronder vind je een overzicht van de lasten per voorstel.</p>
        <br />
        {lastenVergeleken.situatieLasten &&
          lastenVergeleken.situatieLasten.length > 0 &&
          getLastenTable(lastenVergeleken.situatieLasten)}
      </div>
    </Chapter>
  );
};
